import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MessageSquare, DollarSign, TrendingDown, TrendingUp, ShoppingCart, History, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SMS_COST = 0.049;

export default function ClientCredits({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  const fetchCredits = useCallback(() => {
    fetch(`${API}/api/client/credits`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const res = await fetch(`${API}/api/client/credits/purchase`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack_id: packId }),
      });
      const d = await res.json();
      if (d.checkout_url) window.location.href = d.checkout_url;
      else alert(d.detail || t('credits.failedCheckout'));
    } catch (e) { console.error(e); }
    finally { setPurchasing(null); }
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('credits.loading')}</div>;
  if (!data) return <div className="text-red-400 text-center py-12">{t('credits.loadFailed')}</div>;

  const balance = data.balance || 0;
  const approxMessages = Math.floor(balance / SMS_COST);
  const isLow = balance <= (data.low_balance_threshold || 5);
  const packs = Object.entries(data.recharge_packs || {});
  const txns = data.transactions || [];

  return (
    <div className="space-y-6" data-testid="credits-tab">
      {/* Balance Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={`border-2 ${isLow ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50'}`}>
          <CardContent className="pt-5 text-center">
            <DollarSign className={`h-6 w-6 mx-auto mb-1 ${isLow ? 'text-red-500' : 'text-green-600'}`} />
            <p className="text-xs text-gray-500 uppercase">{t('credits.smsBalance')}</p>
            <p className={`text-3xl font-bold mt-1 ${isLow ? 'text-red-600' : 'text-green-700'}`} data-testid="credit-balance">
              ${balance.toFixed(2)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('credits.approxMessages', { count: approxMessages.toLocaleString() })}</p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white">
          <CardContent className="pt-5 text-center">
            <MessageSquare className="h-6 w-6 text-blue-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500 uppercase">{t('credits.costPerSms')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">$0.049</p>
            <p className="text-xs text-gray-500 mt-1">{t('credits.perSegment')}</p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white">
          <CardContent className="pt-5 text-center">
            <TrendingDown className="h-6 w-6 text-orange-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500 uppercase">{t('credits.totalSpent')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">${(data.total_spent || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">{t('credits.allTime')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Low Balance Alert */}
      {isLow && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">{t('credits.lowBalanceTitle')}</p>
              <p className="text-xs text-red-700 mt-1">{t('credits.lowBalanceDesc')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recharge Packs */}
      <Card className="border-gray-200 bg-white">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('credits.addCredits')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {packs.map(([id, pack]) => (
              <button key={id} onClick={() => handlePurchase(id)} disabled={!!purchasing}
                data-testid={`purchase-${id}`}
                className="border-2 border-gray-200 hover:border-blue-400 rounded-xl p-4 text-center transition-all hover:shadow-md">
                <p className="text-2xl font-bold text-gray-900">{pack.label?.replace('SMS Credits', '') || `$${pack.amount}`}</p>
                <p className="text-xs text-gray-500 mt-1">{t('credits.approxMessagesLine', { count: (pack.approx_messages || Math.floor(pack.amount / SMS_COST)).toLocaleString() })}</p>
                <Badge className="mt-2 bg-blue-50 text-blue-600 text-xs">
                  {purchasing === id ? t('credits.processing') : t('credits.buyNow')}
                </Badge>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-3 text-center">{t('credits.ccFootnote')}</p>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      {txns.length > 0 && (
        <Card className="border-gray-200 bg-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900">{t('credits.recentTransactions')}</h3>
            </div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {txns.map((tx, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    {tx.type === 'credit' ? (
                      <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                    )}
                    <span className="text-xs text-gray-700">{tx.description || tx.service}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-mono font-medium ${tx.type === 'credit' ? 'text-green-600' : 'text-red-500'}`}>
                      {tx.type === 'credit' ? '+' : ''}{typeof tx.amount === 'number' ? `$${Math.abs(tx.amount).toFixed(3)}` : tx.amount}
                    </span>
                    <span className="text-[10px] text-gray-400 w-20 text-right">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
